import React, { useState, useEffect } from 'react';
import {
  Layout,
  Row,
  Col,
  Card,
  Statistic,
  DatePicker,
  Button,
  Typography,
  Space,
  message,
  Spin,
  Tag,
  Modal,
  Form,
} from 'antd';
import {
  FireOutlined,
  CrownOutlined,
  StarOutlined,
  TeamOutlined,
  LeftOutlined,
  RightOutlined,
  SearchOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { analyticsApi, DashboardStats } from '../services/api';
import UserBehaviorTable from './UserBehaviorTable';
import ChartSection from './ChartSection';
import PopularPages from './PopularPages';
import ActiveUsersRanking from './ActiveUsersRanking';
import ProUsersList from './ProUsersList';
import NewUsersList from './NewUsersList';
import AlertUsersList from './AlertUsersList';
import FreeUsers5PatientsList from './FreeUsers5PatientsList';
import CombinedUsersGrowthChart from './CombinedUsersGrowthChart';
import JapanMapChart from './JapanMapChart';
import SpecialtyDistributionChart from './SpecialtyDistributionChart';
import UserAcquisitionChart from './UserAcquisitionChart';
import UserSearchModal from './UserSearchModal';
import AnalyticsModeSwitch from './AnalyticsModeSwitch';

const { Header, Content } = Layout;
const { Title, Text } = Typography;

const Dashboard: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [currentDateIndex, setCurrentDateIndex] = useState<number>(0);
  const [dashboardData, setDashboardData] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchModalVisible, setSearchModalVisible] = useState<boolean>(false);
  const [exportModalVisible, setExportModalVisible] = useState<boolean>(false);
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [exportForm] = Form.useForm();

  // 初始化可用日期
  useEffect(() => {
    const initializeDates = async () => {
      try {
        const response = await analyticsApi.getAvailableDates();
        setAvailableDates(response.available_dates);
        setSelectedDate(response.latest_date);
        setCurrentDateIndex(0);
      } catch (error) {
        message.error('获取可用日期失败');
        console.error('Failed to fetch available dates:', error);
      }
    };

    initializeDates();
  }, []);

  // 加载仪表板数据
  useEffect(() => {
    if (selectedDate) {
      loadDashboardData(selectedDate);
    }
  }, [selectedDate]);

  const loadDashboardData = async (date: string) => {
    try {
      setLoading(true);
      const data = await analyticsApi.getDashboardStats(date);
      setDashboardData(data);
    } catch (error) {
      message.error('加载数据失败');
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // 日期导航
  const navigateDate = (direction: number) => {
    const newIndex = currentDateIndex + direction;
    if (newIndex >= 0 && newIndex < availableDates.length) {
      setCurrentDateIndex(newIndex);
      setSelectedDate(availableDates[newIndex]);
    }
  };

  // 检查日期是否可选
  const disabledDate = (current: dayjs.Dayjs | null) => {
    if (!current) return false;
    return !availableDates.includes(current.format('YYYY-MM-DD'));
  };

  // 格式化日期显示
  const formatDateDisplay = (date: string) => {
    return dayjs(date).format('YYYY年MM月DD日');
  };

  return (
    <Layout style={{ minHeight: '100vh', background: '#FDFDFF' }}>
      {/* 头部 */}
      <Header style={{ 
        background: '#FDFDFF', 
        padding: '0 24px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <Row justify="space-between" align="middle" style={{ height: '100%' }}>
          <Col>
            <div>
              <Title level={3} style={{ margin: 0, color: '#000000', lineHeight: 1.2 }}>
                運営統計分析
              </Title>
              <Text style={{ color: '#666666', fontSize: '14px', marginTop: '-2px', display: 'block' }}>
                Operational Analytics Dashboard
              </Text>
            </div>
          </Col>
          
          <Col>
            {/* 搜索和日期选择器 */}
            <Space className="dashboard-toolbar" wrap>
              <AnalyticsModeSwitch />
              <Button
                icon={<DownloadOutlined />}
                onClick={() => setExportModalVisible(true)}
                style={{ borderColor: "#52c41a", color: "#52c41a" }}
              >
                データエクスポート
              </Button>
              <Button
                icon={<SearchOutlined />}
                onClick={() => setSearchModalVisible(true)}
                style={{ borderColor: '#1890ff', color: '#1890ff' }}
              >
                ユーザー検索
              </Button>
              <Space.Compact>
                <Button
                  icon={<LeftOutlined />}
                  disabled={currentDateIndex >= availableDates.length - 1}
                  onClick={() => navigateDate(1)}
                />
                <DatePicker
                  value={selectedDate ? dayjs(selectedDate) : null}
                  format="YYYY-MM-DD"
                  disabledDate={disabledDate}
                  onChange={(date) => {
                    if (date) {
                      const dateStr = date.format('YYYY-MM-DD');
                      if (availableDates.includes(dateStr)) {
                        setSelectedDate(dateStr);
                        setCurrentDateIndex(availableDates.indexOf(dateStr));
                      }
                    }
                  }}
                  placeholder="日付を選択"
                  style={{ width: 140 }}
                />
                <Button
                  icon={<RightOutlined />}
                  disabled={currentDateIndex <= 0}
                  onClick={() => navigateDate(-1)}
                />
              </Space.Compact>
            </Space>
          </Col>
        </Row>
      </Header>

      {/* 主内容 */}
      <Content style={{ padding: '0 24px 24px 24px', background: '#FDFDFF' }}>
        <Spin spinning={loading} size="large">
          {/* Logo展示区域 */}
          <Row justify="center" style={{ margin: 0, padding: 0 }}>
            <Col>
              <img 
                src="/dipceph_logo.png" 
                alt="DipCeph Analytics" 
                style={{ 
                  height: 300,
                  width: 'auto',
                  display: 'block'
                }}
              />
            </Col>
          </Row>

          {/* 核心指标卡片 */}
          <Row gutter={[24, 24]} style={{ marginTop: 0, marginBottom: 32 }}>
            <Col xs={12} lg={6}>
              <Card style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🔥</div>
                <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#000', marginBottom: '8px' }}>
                  {dashboardData?.activeUsers || 0}
                </div>
                <div style={{ fontSize: '16px', color: '#000', marginBottom: '4px' }}>
                  アクティブユーザー数
                </div>
                <div style={{ fontSize: '14px', color: '#666' }}>
                  Active Users
                </div>
              </Card>
            </Col>
            
            <Col xs={12} lg={6}>
              <Card style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '3rem', marginBottom: '16px' }}>💎</div>
                <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#000', marginBottom: '8px' }}>
                  {dashboardData?.paidUsers || 0}
                </div>
                <div style={{ fontSize: '16px', color: '#000', marginBottom: '4px' }}>
                  有料ユーザー数
                </div>
                <div style={{ fontSize: '14px', color: '#666' }}>
                  Paid Users
                </div>
              </Card>
            </Col>
            
            <Col xs={12} lg={6}>
              <Card style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '3rem', marginBottom: '16px' }}>✨</div>
                <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#000', marginBottom: '8px' }}>
                  {dashboardData?.newUsers || 0}
                </div>
                <div style={{ fontSize: '16px', color: '#000', marginBottom: '4px' }}>
                  新規ユーザー数
                </div>
                <div style={{ fontSize: '14px', color: '#666' }}>
                  New Users
                </div>
              </Card>
            </Col>
            
            <Col xs={12} lg={6}>
              <Card style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '3rem', marginBottom: '16px' }}>👥</div>
                <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#000', marginBottom: '8px' }}>
                  {dashboardData?.totalUsers || 0}
                </div>
                <div style={{ fontSize: '16px', color: '#000', marginBottom: '4px' }}>
                  総ユーザー数
                </div>
                <div style={{ fontSize: '14px', color: '#666' }}>
                  Total Users
                </div>
              </Card>
            </Col>
          </Row>

          {/* アクティブユーザー数推移 - 全宽显示 */}
          <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
            <Col xs={24}>
              <ChartSection 
                trendData={dashboardData?.last30DaysTrend || []}
                loading={loading}
              />
            </Col>
          </Row>

          {/* ユーザー増加推移総合 - 全宽显示 */}
          <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
            <Col xs={24}>
              <CombinedUsersGrowthChart selectedDate={selectedDate} />
            </Col>
          </Row>

          {/* 用户计划分布和页面使用统计 - 同一行 */}
          <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <Title level={4} style={{ margin: 0 }}>アクティブユーザープラン分布</Title>
                    <span style={{ color: '#999', fontSize: '14px' }}>Active User Plan Distribution</span>
                  </Space>
                } 
                style={{ height: '100%', minHeight: '400px' }}
              >
                <div style={{ height: '100%', padding: '16px', overflowY: 'auto', maxHeight: '400px' }}>
                  {dashboardData?.planDistribution ? (
                    <div>
                      {(() => {
                        const totalUsers = Object.values(dashboardData.planDistribution).reduce((sum, count) => sum + count, 0);
                        return Object.entries(dashboardData.planDistribution).map(([plan, count]) => {
                          const percentage = totalUsers > 0 ? ((count / totalUsers) * 100).toFixed(1) : '0.0';
                          const progressColor = plan === 'PRO' ? '#faad14' : 
                                              plan === 'ACADEMIC' ? '#1890ff' : 
                                              plan === 'PARTNER' ? '#52c41a' :
                                              plan === 'INTERNAL' ? '#722ed1' : '#d9d9d9';
                          
                          return (
                            <div key={plan} style={{ 
                              marginBottom: '12px',
                              padding: '12px',
                              background: '#fafafa',
                              borderRadius: '6px'
                            }}>
                              <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                marginBottom: '8px'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                  <Tag color={plan === 'PRO' ? 'gold' : 
                                            plan === 'ACADEMIC' ? 'blue' : 
                                            plan === 'PARTNER' ? 'green' :
                                            plan === 'INTERNAL' ? 'purple' : 'default'}>
                                    {plan}
                                  </Tag>
                                  <span style={{ marginLeft: '8px', fontSize: '14px', color: '#666' }}>
                                    {count}人
                                  </span>
                                </div>
                                <div style={{ fontSize: '20px', fontWeight: 'bold', color: progressColor }}>
                                  {percentage}%
                                </div>
                              </div>
                              
                              {/* 进度条 */}
                              <div style={{ 
                                width: '100%', 
                                height: '8px', 
                                background: '#f0f0f0', 
                                borderRadius: '4px',
                                overflow: 'hidden'
                              }}>
                                <div style={{ 
                                  width: `${percentage}%`, 
                                  height: '100%', 
                                  background: progressColor,
                                  borderRadius: '4px',
                                  transition: 'width 0.3s ease'
                                }} />
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  ) : (
                    <div style={{ 
                      height: '100%', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      color: '#999'
                    }}>
                      {loading ? '読み込み中...' : 'データがありません'}
                    </div>
                  )}
                </div>
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <PopularPages 
                data={dashboardData?.topPages || []}
                loading={loading}
              />
            </Col>
          </Row>

          {/* 用户地域分布地图和专业分布 - 同一行 */}
          <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
            <Col xs={24} lg={12}>
              <JapanMapChart selectedDate={selectedDate} />
            </Col>
            <Col xs={24} lg={12}>
              <SpecialtyDistributionChart selectedDate={selectedDate} />
            </Col>
          </Row>

          {/* 用户获取情况 - 全宽显示 */}
          <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
            <Col xs={24}>
              <UserAcquisitionChart selectedDate={selectedDate} />
            </Col>
          </Row>

          {/* PRO用户和新用户列表 - 同一行 */}
          <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
            <Col xs={24} lg={12}>
              <div style={{ height: '500px' }}>
                <ProUsersList />
              </div>
            </Col>
            <Col xs={24} lg={12}>
              <div style={{ height: '500px' }}>
                <NewUsersList selectedDate={selectedDate} />
              </div>
            </Col>
          </Row>

          {/* 提示框用户和5名患者FREE用户列表 - 同一行 */}
          <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
            <Col xs={24} lg={12}>
              <div style={{ height: '500px' }}>
                <AlertUsersList />
              </div>
            </Col>
            <Col xs={24} lg={12}>
              <div style={{ height: '500px' }}>
                <FreeUsers5PatientsList />
              </div>
            </Col>
          </Row>

          {/* 活跃用户排名 */}
          <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
            <Col xs={24}>
              <ActiveUsersRanking />
            </Col>
          </Row>


          {/* 用户行为表格 */}
          <UserBehaviorTable 
            selectedDate={selectedDate}
            loading={loading}
          />
        </Spin>
      </Content>

      
      {/* 用户数据导出模态框 */}
      <Modal
        title="データエクスポート"
        open={exportModalVisible}
        onCancel={() => setExportModalVisible(false)}
        onOk={async () => {
          try {
            const values = await exportForm.validateFields();
            setExportLoading(true);
            
            const startDate = values.dateRange[0].format("YYYY-MM-DD");
            const endDate = values.dateRange[1].format("YYYY-MM-DD");
            
            const blob = await analyticsApi.exportUsers(startDate, endDate);
            
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `user_export_${startDate}_${endDate}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            message.success("データのエクスポートが完了しました");
            setExportModalVisible(false);
          } catch (error) {
            console.error("Export error:", error);
            message.error("エクスポートに失敗しました");
          } finally {
            setExportLoading(false);
          }
        }}
        confirmLoading={exportLoading}
        okText="エクスポート"
        cancelText="キャンセル"
      >
        <Form form={exportForm} layout="vertical">
          <Form.Item
            name="dateRange"
            label="期間を選択"
            rules={[{ required: true, message: "期間を選択してください" }]}
          >
            <DatePicker.RangePicker 
              style={{ width: "100%" }}
              format="YYYY-MM-DD"
            />
          </Form.Item>
        </Form>
      </Modal>
      {/* 用户搜索模态框 */}
      <UserSearchModal
        visible={searchModalVisible}
        onClose={() => setSearchModalVisible(false)}
      />
    </Layout>
  );
};

export default Dashboard;
